import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { getBookingSchedule } from "../services/booking.service";
import { getUnits } from "../services/unit.service";
import {
  BookingScheduleResponse,
  ScheduleSlotSchema,
  SlotDisplayStatus,
} from "../types/booking.type";
import { UnitSchema } from "../types/unit.type";
import {
  formatDateForInput,
  formatSlotDateLabel,
  formatTimeRange,
  getSlotDisplayStatus,
} from "../utils/schedule";

const STATUS_CONFIG: Record<
  SlotDisplayStatus,
  {
    label: string;
    color?: "success" | "primary" | "warning" | "default";
    chipSx?: { bgcolor: string; color: string; fontWeight: number };
  }
> = {
  available: { label: "Available", color: "success" },
  booked: { label: "Booked", color: "primary" },
  pending: { label: "Pending", color: "warning" },
  failed: { label: "Failed", color: "default" },
  cleaning: {
    label: "Cleaning",
    chipSx: { bgcolor: "#fdd835", color: "#1a1a1a", fontWeight: 600 },
  },
};

const unwrap = <T,>(response: unknown): T =>
  (response as { result?: T })?.result ?? (response as T);

const Schedule: React.FC = () => {
  const [units, setUnits] = useState<UnitSchema[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [selectedDate, setSelectedDate] = useState(formatDateForInput(new Date()));
  const [schedule, setSchedule] = useState<BookingScheduleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedUnit = useMemo(
    () => units.find((u) => u.id === selectedUnitId) ?? null,
    [units, selectedUnitId]
  );

  const timezone = schedule?.timezone ?? selectedUnit?.timezone ?? "UTC";

  const loadUnits = useCallback(async () => {
    try {
      const response = await getUnits();
      const allUnits = unwrap<UnitSchema[]>(response) ?? [];
      setUnits(allUnits);
      if (allUnits.length > 0 && !selectedUnitId) {
        setSelectedUnitId(allUnits[0].id);
      }
    } catch {
      setError("Failed to load units.");
    }
  }, [selectedUnitId]);

  const loadSchedule = useCallback(async () => {
    if (!selectedUnitId || !selectedDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getBookingSchedule({
        unit_id: selectedUnitId,
        date: selectedDate,
      });
      setSchedule(unwrap<BookingScheduleResponse>(response));
    } catch {
      setError("Failed to load schedule.");
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, [selectedUnitId, selectedDate]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  useEffect(() => {
    if (selectedUnitId) {
      loadSchedule();
    }
  }, [selectedUnitId, selectedDate, loadSchedule]);

  const renderSlotDetails = (slot: ScheduleSlotSchema) => {
    if (!slot.booking) return "—";

    const parts = [`Status: ${slot.booking.status}`];
    if (slot.booking.user_id) {
      parts.push(`User: ${slot.booking.user_id}`);
    }
    if (slot.booking.expires_at && slot.status === "pending") {
      parts.push(
        `Expires ${new Date(slot.booking.expires_at).toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })}`
      );
    }
    return parts.join(" · ");
  };

  const slots = schedule?.slots ?? [];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Schedule
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Daily timeline (8 AM – 10 PM) with booking status per hourly slot. Slots
        adjacent to booked or pending sessions are shown as cleaning buffers.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel id="unit-select-label">Unit</InputLabel>
            <Select
              labelId="unit-select-label"
              label="Unit"
              value={selectedUnitId}
              onChange={(e) => setSelectedUnitId(e.target.value)}
            >
              {units.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Date"
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSchedule}
            disabled={loading || !selectedUnitId}
          >
            Refresh
          </Button>
        </Box>

        {selectedUnitId && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {formatSlotDateLabel(selectedDate, timezone)} · {timezone}
          </Typography>
        )}
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Booking ID</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {slots.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No slots returned for this day.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                slots.map((slot, index) => {
                  const displayStatus = getSlotDisplayStatus(slots, index);
                  const config = STATUS_CONFIG[displayStatus];
                  return (
                    <TableRow key={slot.start_time}>
                      <TableCell>
                        {formatTimeRange(slot.start_time, slot.end_time, timezone)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={config.label}
                          color={config.color}
                          size="small"
                          sx={config.chipSx}
                        />
                      </TableCell>
                      <TableCell>
                        {displayStatus === "cleaning" ? "—" : slot.booking?.id ?? "—"}
                      </TableCell>
                      <TableCell>
                        {displayStatus === "cleaning" ? "—" : renderSlotDetails(slot)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Schedule;
